import type {
  ContentOptimizationChannel,
  ContentOptimizationComponentName,
  EmailComponentName,
  PushComponentName,
  SmsComponentName,
} from "./types";
import { emailComponentNames, pushComponentNames, smsComponentNames } from "./types";

export interface ChannelComponentConfig {
  names: readonly ContentOptimizationComponentName[];
}

export interface ChannelComponentConfigOptions {
  imageVariantsEnabled: boolean;
  pushImageVariantsEnabled?: boolean;
  rcsImageVariantsEnabled?: boolean;
  rcsImageEligible?: boolean;
}

export const channelComponentConfig: Record<ContentOptimizationChannel, ChannelComponentConfig> = {
  email: { names: emailComponentNames },
  push: { names: pushComponentNames },
  sms: { names: smsComponentNames },
};

const maxComponentsWithRaisedLimits = 4;

// Composer max-components caps in effect before AIML-5383.
const defaultMaxComponents: Record<ContentOptimizationChannel, number> = {
  email: 3,
  push: pushComponentNames.length,
  sms: 2,
};

/**
 * Email Add-menu / allowlist names, with Image included only when image variants are enabled.
 */
const getEmailComponentNames = ({
  imageVariantsEnabled,
}: {
  imageVariantsEnabled: boolean;
}): readonly EmailComponentName[] => {
  if (imageVariantsEnabled) {
    return emailComponentNames;
  }
  return emailComponentNames.filter((name) => name !== "Image");
};

/**
 * Push Add-menu / allowlist names, with Image included only when Push image variants are enabled.
 */
const getPushComponentNames = ({
  pushImageVariantsEnabled,
}: {
  pushImageVariantsEnabled: boolean;
}): readonly PushComponentName[] => {
  if (pushImageVariantsEnabled) {
    return pushComponentNames;
  }
  return pushComponentNames.filter((name) => name !== "Image");
};

const getSmsComponentNames = ({
  rcsImageVariantsEnabled,
  rcsImageEligible,
}: {
  rcsImageVariantsEnabled: boolean;
  rcsImageEligible: boolean;
}): readonly SmsComponentName[] => {
  if (rcsImageVariantsEnabled && rcsImageEligible) {
    return smsComponentNames;
  }
  return smsComponentNames.filter((name) => name !== "Image");
};

/**
 * Composer max-components cap for a channel. The `increasedLimitsEnabled` argument
 * threads through the `content_optimization_raised_component_limits` rollout flipper
 * (AIML-5383). React callers should prefer the `useMaxCoComponents` hook, which reads
 * the flipper directly.
 */
export const getMaxComponents = (
  channel: ContentOptimizationChannel,
  increasedLimitsEnabled: boolean,
  pushImageVariantsEnabled: boolean,
  {
    rcsImageVariantsEnabled = false,
    rcsImageEligible = false,
  }: { rcsImageVariantsEnabled?: boolean; rcsImageEligible?: boolean } = {}
): number => {
  if (increasedLimitsEnabled) {
    return maxComponentsWithRaisedLimits;
  }
  if (channel === "push") {
    return getPushComponentNames({ pushImageVariantsEnabled }).length;
  }
  if (channel === "sms") {
    if (rcsImageVariantsEnabled && rcsImageEligible) {
      return defaultMaxComponents.sms + 1;
    }
    return defaultMaxComponents.sms;
  }
  return defaultMaxComponents[channel];
};

/**
 * Channel allowlist config with Image filtered by the channel-specific image-variants flippers.
 */
export const getChannelComponentConfig = (
  channel: ContentOptimizationChannel,
  {
    imageVariantsEnabled,
    pushImageVariantsEnabled = false,
    rcsImageVariantsEnabled = false,
    rcsImageEligible = false,
  }: ChannelComponentConfigOptions
): ChannelComponentConfig => {
  const base = channelComponentConfig[channel];
  if (channel === "email") {
    return {
      ...base,
      names: getEmailComponentNames({ imageVariantsEnabled }),
    };
  }
  if (channel === "push") {
    return {
      ...base,
      names: getPushComponentNames({ pushImageVariantsEnabled }),
    };
  }
  if (channel === "sms") {
    return {
      ...base,
      names: getSmsComponentNames({ rcsImageVariantsEnabled, rcsImageEligible }),
    };
  }
  return base;
};
